import { useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, ArrowRight, Menu, X, MapPin, Mail, Phone, Plus } from "lucide-react";
import HeroCanvas from "./HeroCanvas";
import CustomCursor from "./CustomCursor";
import { SERVICES, PROJECTS, MARQUEE } from "./data";

/* ---------------- Hooks ---------------- */

function useScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? window.scrollY / max : 0);
    };
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  return p;
}

function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, seen];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, seen] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: seen ? 1 : 0,
      transform: seen ? "translateY(0)" : "translateY(28px)",
      transition: `opacity .9s cubic-bezier(.2,.8,.2,1) ${delay}ms, transform .9s cubic-bezier(.2,.8,.2,1) ${delay}ms`,
    }}>{children}</div>
  );
}

function Counter({ to, suffix = "", prefix = "" }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      const start = performance.now();
      const dur = 1600;
      const tick = (t) => {
        const k = Math.min(1, (t - start) / dur);
        const eased = 1 - Math.pow(1 - k, 3);
        setN(Math.floor(eased * to));
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      obs.disconnect();
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{prefix}{n.toLocaleString()}{suffix}</span>;
}

/* ---------------- Navigation ---------------- */

const NAV = [
  { id: "work", label: "Work" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <header data-testid="site-nav" className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#1f1f22]" : "bg-transparent"}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <button data-testid="logo-home-btn" onClick={() => scrollTo("top")} className="flex items-center gap-2 group">
          <div className="w-7 h-7 relative">
            <div className="absolute inset-0 border border-white/80 group-hover:border-[var(--brand)] transition-colors" />
            <div className="absolute inset-[4px] bg-[var(--brand)] group-hover:rotate-45 transition-transform duration-500" />
          </div>
          <span className="font-display text-lg tracking-tight">SHOONYA</span>
          <span className="font-mono text-[10px] text-[var(--text-2)] mt-1">/STUDIO</span>
        </button>
        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}-btn`} onClick={() => scrollTo(n.id)} className="link-underline font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-1)] hover:text-white transition">
              {n.label}
            </button>
          ))}
        </nav>
        <button data-testid="nav-cta-btn" onClick={() => scrollTo("contact")} className="hidden md:inline-flex btn-stark">Start a Project <ArrowUpRight size={16} /></button>
        <button data-testid="nav-mobile-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#1f1f22] bg-black/95">
          <div className="flex flex-col p-6 gap-5">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`mnav-${n.id}`} onClick={() => scrollTo(n.id)} className="text-left font-display text-2xl">{n.label}</button>
            ))}
            <button data-testid="mnav-cta" onClick={() => scrollTo("contact")} className="btn-stark mt-4 justify-center">Start a Project</button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------------- Scroll progress bar ---------------- */

function ScrollRail() {
  const p = useScrollProgress();
  return (
    <div data-testid="scroll-rail" className="fixed left-0 top-0 bottom-0 w-[3px] z-40 pointer-events-none">
      <div className="absolute top-0 left-0 w-full bg-[var(--brand)]" style={{ height: `${p * 100}%`, transition: "height 80ms linear" }} />
    </div>
  );
}

/* ---------------- Hero ---------------- */

function Hero() {
  const ref = useRef(null);
  const [t, setT] = useState(0);
  useEffect(() => {
    const on = () => {
      if (!ref.current) return;
      const h = ref.current.offsetHeight;
      setT(Math.min(1, Math.max(0, window.scrollY / h)));
    };
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const style = {
    opacity: 1 - t * 1.3,
    transform: `translateY(${t * -60}px)`,
    filter: `blur(${t * 3}px)`,
  };
  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <div className="absolute inset-0" style={{ opacity: 1 - t * 0.6 }}>
        <HeroCanvas />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 60%, transparent 30%, rgba(5,5,5,0.85) 80%)" }} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-40 pb-16" style={style}>
        <h1 className="font-display text-[13vw] md:text-[9vw] leading-[0.88] tracking-[-0.045em] max-w-[16ch] reveal" style={{ animationDelay: "120ms" }}>
          We engineer<br />
          <span className="text-[var(--brand)]">the impossible</span><br />
          into product.
        </h1>
        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end reveal" style={{ animationDelay: "320ms" }}>
          <p className="md:col-span-5 text-lg text-[var(--text-1)] leading-relaxed max-w-[44ch]">
            A premium software &amp; AI studio building category-defining products with fintechs, healthtechs and founders across 23 countries.
          </p>
          <div className="md:col-span-5 md:col-start-8 flex flex-wrap gap-3">
            <button data-testid="hero-cta-primary" onClick={() => scrollTo("contact")} className="btn-stark">Start a Project <ArrowUpRight size={16} /></button>
            <button data-testid="hero-cta-secondary" onClick={() => scrollTo("work")} className="btn-ghost">See the Work</button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 left-0 right-0 z-10 flex items-center justify-between px-6 md:px-10 max-w-[1400px] mx-auto font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">
        <span>Move cursor · particles respond</span>
        <span>↓ scroll</span>
      </div>
    </section>
  );
}

/* ---------------- Marquee ---------------- */

function Marquee() {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div data-testid="clients-marquee" className="relative border-y border-[#1f1f22] py-6 overflow-hidden">
      <div className="marquee-track flex gap-12 whitespace-nowrap">
        {items.map((m, i) => (
          <div key={i} className="flex items-center gap-12 text-[var(--text-1)]">
            <span className="font-display text-2xl md:text-3xl tracking-tight">{m}</span>
            <span className="text-[var(--brand)] font-mono">✕</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Stats (compact) ---------------- */

function Stats() {
  const items = [
    { n: 84, label: "Products Shipped" },
    { n: 23, label: "Countries Served" },
    { n: 11, label: "Years of Craft" },
    { n: 420, prefix: "$", suffix: "M", label: "Client Revenue Impact" },
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((s, i) => (
          <div key={s.label} data-testid={`stat-${i}`} className="border-t border-[var(--border-hi)] pt-4">
            <div className="ticker"><Counter to={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} /></div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)] mt-2">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Services — sticky split with cross-fading visual ---------------- */

const SERVICE_VISUALS = [
  "https://images.unsplash.com/photo-1759159347827-de3a54002de7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1773349807434-374473797148?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1656231267330-f605c1c16a57?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1760978632114-0939f0d60045?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1720135885007-454165745e21?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1771873680097-7506773a2b02?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

function Services() {
  const [active, setActive] = useState(0);
  const itemRefs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const idx = Number(e.target.getAttribute("data-idx"));
          if (!Number.isNaN(idx)) setActive(idx);
        }
      });
    }, { rootMargin: "-45% 0px -45% 0px", threshold: 0 });
    itemRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return (
    <section id="services" data-testid="services-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div>
          <div className="overline mb-4">/ 01 — CAPABILITIES</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95] max-w-[14ch]">What we do, with obsessive depth.</h2>
        </div>
        <p className="max-w-[38ch] text-[var(--text-1)]">Six disciplines, one integrated team. No subcontracting, no handoffs — only senior engineers and designers.</p>
      </div>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {SERVICES.map((s, i) => (
            <div
              key={s.num}
              data-idx={i}
              data-testid={`service-${i}`}
              ref={(el) => (itemRefs.current[i] = el)}
              className={`border-t border-[var(--border-hi)] py-10 md:py-12 transition-opacity duration-500 ${active === i ? "opacity-100" : "opacity-40"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.2em] text-[var(--brand)]">{s.num}</span>
                <Plus size={18} className={`transition-all duration-500 ${active === i ? "rotate-90 text-[var(--brand)]" : "text-[var(--text-2)]"}`} />
              </div>
              <h3 className="font-display text-3xl md:text-5xl mt-4 tracking-tight">{s.title}</h3>
              <p className="mt-4 text-[var(--text-1)] leading-relaxed max-w-[52ch]">{s.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] border border-[var(--border-hi)] px-2.5 py-1 text-[var(--text-1)]">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-5 hidden lg:block">
          <div className="sticky top-28 aspect-[4/5] overflow-hidden border border-[var(--border)] bg-[var(--bg-1)]">
            {SERVICE_VISUALS.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale-[30%]"
                style={{ opacity: active === i ? 1 : 0, transition: "opacity .8s cubic-bezier(.2,.8,.2,1)" }}
              />
            ))}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0) 55%, rgba(5,5,5,.75) 100%)" }} />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">Now viewing</span>
              <span className="font-display text-xl">{SERVICES[active]?.title}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Portfolio — pinned horizontal scroll ---------------- */

function Portfolio() {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapRef.current;
      if (!el || !trackRef.current) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      const moveX = (trackRef.current.scrollWidth - window.innerWidth + 48) * p;
      trackRef.current.style.transform = `translate3d(${-moveX}px, 0, 0)`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section id="work" data-testid="portfolio-section">
      <div ref={wrapRef} className="relative" style={{ height: `${100 + PROJECTS.length * 55}vh` }}>
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-24 md:pt-28">
            <div className="flex items-end justify-between flex-wrap gap-6">
              <div>
                <div className="overline mb-3">/ 02 — SELECTED WORK · {String(Math.round(progress * (PROJECTS.length - 1)) + 1).padStart(2, "0")} / {String(PROJECTS.length).padStart(2, "0")}</div>
                <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95] max-w-[16ch]">Proof, not pitch decks.</h2>
              </div>
              <p className="max-w-[38ch] text-[var(--text-1)] hidden md:block">Scroll — the case studies slide sideways. Numbers we can defend in a boardroom.</p>
            </div>
            <div className="mt-6 h-[2px] w-full bg-[var(--border)] relative">
              <div className="absolute left-0 top-0 h-full bg-[var(--brand)]" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
          <div className="flex-1 overflow-hidden mt-8">
            <div ref={trackRef} className="h-full flex gap-6 md:gap-8 pl-6 md:pl-10 pr-6 md:pr-10 will-change-transform">
              {PROJECTS.map((p, i) => (
                <article
                  key={p.client}
                  data-testid={`project-${i}`}
                  className="proj-card relative flex-shrink-0 w-[86vw] sm:w-[70vw] md:w-[52vw] lg:w-[42vw] xl:w-[40vw] h-[72vh] overflow-hidden flex flex-col"
                >
                  <div className="relative flex-1 overflow-hidden bg-[var(--bg-2)]">
                    <img loading="lazy" src={p.image} alt={p.client} className="proj-img w-full h-full object-cover opacity-80 grayscale-[20%]" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0) 40%, rgba(5,5,5,0.9) 100%)" }} />
                    <div className="absolute top-5 left-5 right-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
                      <span className="text-white/80">{p.industry}</span>
                      <span className="text-[var(--brand)]">{p.year}</span>
                    </div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="flex items-end justify-between gap-5">
                        <h3 className="font-display text-3xl md:text-5xl tracking-tight">{p.client}</h3>
                        <div className="text-right">
                          <div className="font-display text-2xl md:text-4xl text-[var(--brand)] leading-none">{p.metric}</div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 mt-1">{p.metricLabel}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 grid grid-cols-5 gap-4 border-t border-[var(--border)]">
                    <div className="col-span-3">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--text-2)]">Problem</div>
                      <p className="mt-1.5 text-sm text-[var(--text-0)] leading-snug">{p.problem}</p>
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--text-2)] mt-3">Solution</div>
                      <p className="mt-1.5 text-sm text-[var(--text-1)] leading-snug">{p.solution}</p>
                    </div>
                    <div className="col-span-2 border-l border-[var(--border)] pl-4">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--text-2)]">Impact</div>
                      <p className="mt-1.5 text-sm text-[var(--text-0)] leading-snug">{p.side}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {p.stack.map((s) => (
                          <span key={s} className="font-mono text-[9px] uppercase tracking-[0.15em] border border-[var(--border-hi)] px-2 py-0.5 text-[var(--text-1)]">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
              <div className="flex-shrink-0 w-[20vw]" />
            </div>
          </div>
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-8 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)] flex items-center justify-between">
            <span>← drag or scroll</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and a short brief.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast.success("Brief received. We'll respond within 24 hours.");
      setForm({ name: "", email: "", company: "", budget: "", message: "" });
    }, 900);
  };
  return (
    <section id="contact" data-testid="contact-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 border-t border-[var(--border)]">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="overline mb-4">/ 03 — START A PROJECT</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.92]">Let's build<br /><span className="text-[var(--brand)]">something real.</span></h2>
          <p className="mt-6 text-[var(--text-1)] text-lg max-w-[40ch]">Tell us about your problem. A senior principal replies within one business day — never a sales rep.</p>
          <div className="mt-10 space-y-4 text-sm">
            <div className="flex items-start gap-3 text-[var(--text-1)]"><MapPin size={16} className="text-[var(--brand)] mt-1" /><span>12th Floor, Prestige Atlas<br />Koramangala, Bengaluru 560095, India</span></div>
            <div className="flex items-center gap-3 text-[var(--text-1)]"><Mail size={16} className="text-[var(--brand)]" /><a data-testid="contact-email-link" href="mailto:hello@shoonya.studio" className="link-underline">hello@shoonya.studio</a></div>
            <div className="flex items-center gap-3 text-[var(--text-1)]"><Phone size={16} className="text-[var(--brand)]" /><a data-testid="contact-phone-link" href="tel:+918697578228" className="link-underline">+91 86975 78228</a></div>
          </div>
        </div>
        <form onSubmit={onSubmit} data-testid="contact-form" className="md:col-span-7 md:col-start-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 border-t border-[var(--border-hi)] pt-8">
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)]">Name</label>
            <input data-testid="contact-name" className="field mt-2" value={form.name} onChange={onChange("name")} placeholder="Your full name" />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)]">Email</label>
            <input data-testid="contact-email" type="email" className="field mt-2" value={form.email} onChange={onChange("email")} placeholder="you@company.com" />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)]">Company</label>
            <input data-testid="contact-company" className="field mt-2" value={form.company} onChange={onChange("company")} placeholder="Company / org" />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)]">Budget</label>
            <select data-testid="contact-budget" className="field mt-2 bg-[var(--bg-0)]" value={form.budget} onChange={onChange("budget")}>
              <option value="">Select a range</option>
              <option>Under $50k</option>
              <option>$50k – $150k</option>
              <option>$150k – $500k</option>
              <option>$500k+</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)]">Project Brief</label>
            <textarea data-testid="contact-message" rows={4} className="field mt-2 resize-none" value={form.message} onChange={onChange("message")} placeholder="What are you building? What's in your way?" />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">By submitting, you agree to our privacy policy.</p>
            <button data-testid="contact-submit" disabled={loading} className="btn-stark disabled:opacity-60">
              {loading ? "Sending..." : (<>Send Brief <ArrowRight size={16} /></>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */

function Footer() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer data-testid="site-footer" className="border-t border-[var(--border)] bg-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="font-display text-5xl md:text-7xl tracking-[-0.04em] leading-[0.9]">SHOONYA<span className="text-[var(--brand)]">.</span></div>
          </div>
          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)] mb-3">Sections</div>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button data-testid={`footer-nav-${n.id}`} onClick={() => scrollTo(n.id)} className="link-underline text-left">{n.label}</button>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)] mb-3">Reach</div>
            <ul className="space-y-2 text-sm">
              <li><a data-testid="footer-email" href="mailto:hello@shoonya.studio" className="link-underline">hello@shoonya.studio</a></li>
              <li><a data-testid="footer-phone" href="tel:+918697578228" className="link-underline">+91 86975 78228</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">
          <span>© {new Date().getFullYear()} Shoonya Studio</span>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- App ---------------- */

function App() {
  useEffect(() => {
    document.title = "Shoonya Studio — Software & AI Development · Bengaluru, India";
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.0,
    });
    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // smooth in-page anchor clicks via Lenis
    const onAnchorClick = (e) => {
      const a = e.target.closest?.('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = document.getElementById(id);
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -40 }); }
    };
    document.addEventListener("click", onAnchorClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onAnchorClick);
    };
  }, []);
  return (
    <div className="App grain">
      <Toaster theme="dark" position="bottom-right" richColors />
      <CustomCursor />
      <ScrollRail />
      <Nav />
      <Hero />
      <Marquee />
      <Stats />
      <Services />
      <Portfolio />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
